import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Notice, Spinner } from "@/components/common/States";
import { ACCESS_STATE } from "@/lib/labels";
import { api, errorMessage } from "@/lib/api";

// Tindakan founder atas tagihan. Tanpa unggah bukti: yang dicatat hanyalah penilaian founder,
// dan alasannya wajib agar jejak audit tidak bergantung pada pengunggah berkas.
export const ACTIONS = {
  CLAIMED: { label: "Tandai sudah bayar ke reseller", ringkas: "sudah bayar", bahaya: false,
     penjelasan: "Lapis pertama: sekolah sudah menyetor ke reseller. Akun belum lunas ke founder, jadi setelah tanggal 3 ia berstatus “reseller belum setor” dan bisa terkunci pada tanggal 8 sampai founder menekan Aktifkan." },
  ACTIVE: { label: "Aktifkan akun", ringkas: "aktifkan", bahaya: false,
     penjelasan: "Lapis kedua: uang kulakan reseller benar-benar sampai ke founder. Akun berstatus lunas untuk periode ini dan tidak bisa terkunci oleh tanggal." },
  WAIVED: { label: "Gratiskan", ringkas: "gratiskan", bahaya: false,
     penjelasan: "Tagihan dihapus tanpa pembayaran dan akses langsung aktif. Tidak tercatat sebagai piutang reseller, dan alasannya wajib diisi agar bisa ditelusuri." },
  PENDING: { label: "Batal penilaian", ringkas: "batal", bahaya: true,
     penjelasan: "Kembalikan ke belum bayar: tagihan jatuh ke tenggang biasa dan bisa terkunci mulai tanggal 4. Jejak penilaian sebelumnya dihapus dari baris tagihan." },
};

export function ActionDialog({ open, onOpenChange, action, jumlah, scope, targetLabel, periode, bukti, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  if (!action) return null;
  const meta = ACTIONS[action];

  const tutup = (o) => {
    if (!o) {
      setReason("");
      setErr("");
    }
    onOpenChange(o);
  };

  const submit = async () => {
    if (reason.trim().length < 5) return setErr("Alasan wajib diisi, minimal 5 karakter, dan tercatat di audit.");
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/platform/billing/bills/status", {
        periode, status: action, reason: reason.trim(), ...scope,
      });
      const kondisi = ACCESS_STATE[data.akses_setelah]?.label;
      toast.success(`${meta.ringkas}: ${data.diubah} akun diubah${data.sudah_benar ? `, ${data.sudah_benar} sudah berstatus begitu` : ""}${kondisi ? ` · kondisi akses: ${kondisi}` : ""}.`);
      setReason("");
      onOpenChange(false);
      onDone(data);
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={tutup}>
      <DialogContent data-testid="billing-action-dialog">
        <DialogHeader>
          <DialogTitle>{meta.label}{jumlah ? ` — ${jumlah} akun` : ` — seluruh akun ${targetLabel}`}</DialogTitle>
          <DialogDescription>{meta.penjelasan}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Notice tone="muted" testId="billing-action-no-proof">
            <p className="font-semibold">Tidak perlu mengunggah bukti pembayaran.</p>
            <p className="mt-1">Yang dicatat adalah penilaian founder; sistem memeriksa sendiri jumlah akun dan setorannya.
              Kalau angka setoran dan jumlah akun tidak cocok, founder mengecek langsung ke reseller sebelum mengubah status.</p>
          </Notice>
          {!jumlah && <Notice tone="warn" testId="billing-action-collective">
            Tindakan ini mengenai semua tagihan {targetLabel} pada periode {periode}, termasuk baris yang tidak tampil di layar.
          </Notice>}
          {bukti && <p className="text-sm text-muted-foreground">{bukti}</p>}
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="billing-action-reason">Alasan (wajib, tercatat di audit)</label>
            <Textarea id="billing-action-reason" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Contoh: bendahara sekolah setor kolektif 12 November" data-testid="billing-action-reason-input" />
          </div>
          {err && <Notice tone="danger" testId="billing-action-error">{err}</Notice>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => tutup(false)} data-testid="billing-action-cancel">Batal</Button>
          <Button variant={meta.bahaya ? "destructive" : "default"} onClick={submit} disabled={busy} data-testid="billing-action-submit">
            {busy && <Spinner />} {meta.label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
